import {
  ChevronDownIcon,
  ChevronRightIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexReferral} from '@/types';
import type {ReferralRowType} from '@/types/ReferralListProps';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import ReferralSummary from './ReferralSummary';

const ReferralTable = ({
  items,
  handlePayment,
  handleBlockPayout,
}: {
  items: AfriexReferral[];
  handlePayment: (referralId: string) => void;
  handleBlockPayout: (referralId: string) => void;
}) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>From</Th>
          <Th>To</Th>
          <Th>Amount</Th>
          <Th>Status</Th>
          <Th>Date</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>

      <TBody>
        {items.map((item, index) => (
          <ReferralTableRow
            key={index}
            item={item}
            handlePayment={handlePayment}
            handleBlockPayout={handleBlockPayout}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
};

const ReferralTableRow = ({
  item,
  handlePayment,
  handleBlockPayout,
  index,
}: ReferralRowType) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow =
    typeof index !== 'undefined' && index % 2 === 0
      ? ''
      : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              )}
            </div>
          </Button>
        </Td>

        <Td className={`font-medium`}>{item.fromUserName}</Td>
        <Td>{item.toUserName?.toLowerCase()}</Td>
        <Td>
          {formatValues(Number(item.cumulativeTransactionAmount) || 0, 'money')}
        </Td>
        <Td>{item.status?.toUpperCase()}</Td>
        <Td>{formatDate.DDMonYYYY(item.updatedAt?.toString() || '')}</Td>

        <Td>
          <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
            {isExpanded ? (
              <ChevronDownIcon
                className="w-5 h-5"
                onClick={() => setIsExpanded(false)}
              />
            ) : (
              <ChevronRightIcon
                className="w-5 h-5"
                onClick={() => setIsExpanded(true)}
              />
            )}
          </div>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={7} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <ReferralSummary
                item={item}
                handlePayment={handlePayment}
                handleBlockPayout={handleBlockPayout}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default ReferralTable;
